import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, AlertTriangle, RotateCw, ArrowRight, Crown, BadgeCheck, QrCode,
  Trophy, Settings as SettingsIcon, Store, BarChart3, Award, User, ListChecks, ShieldHalf,
  Gauge, MapPin, Swords, Coins,
} from "lucide-react";
import { api, qrUrl, SITE_ORIGIN } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShareButton } from "../components/ShareButton";
import { StatusBadge, SimBadge } from "../components/Badges";
import { RankChange } from "../components/Rating";
import { CinematicPortrait } from "../components/CinematicPortrait";
import { Flag } from "../components/Flag";
import { ClubCrest } from "../components/ClubCrest";
import { Strike5Card, DisciplineCard, OverallCard, OverallShowcaseTimeline } from "../components/DisciplineCards";
import { RecordsHeld } from "../components/RecordsHeld";
import { PlayerTransferPanel } from "../components/Transfers";
import { PlayerSeasonRacePanel } from "../components/SeasonRace";
import { PerformanceOverviewBox, CurrentSeasonBox, LadderRatingGrid } from "../components/PassportPerformance";
import { CollectionShowcase } from "../components/CollectionShowcase";
import { ConnectedActivity } from "../components/ConnectedActivity";
import { SportsbookPassport } from "../components/SportsbookPassport";

const CONTAINER = "mx-auto max-w-[1500px] px-4 sm:px-8 lg:px-12";

const TABS = [
  { key: "overview", label: "Overview", Icon: User },
  { key: "statistics", label: "Statistics", Icon: BarChart3 },
  { key: "achievements", label: "Achievements", Icon: Award },
  { key: "career", label: "Career", Icon: ListChecks },
];

// One premium metric tile (Rating / World Rank / Country Rank / Role).
function MetricCard({ label, sub, children, testid, accent = "text-slate-50", Icon, to, ariaLabel }) {
  const base = "relative rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.18)] p-4 sm:p-5 overflow-hidden";
  const inner = (
    <>
      <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.1), transparent 70%)" }} />
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">{label}</div>
        {Icon && (
          <span className="shrink-0 grid place-items-center w-8 h-8 rounded-full bg-[#0c0c0c] border border-[#E5B842]/30">
            <Icon className="w-4 h-4 text-[#E5B842]" strokeWidth={1.75} />
          </span>
        )}
      </div>
      <div className={`font-heading text-3xl sm:text-4xl font-black tabular-nums leading-none ${accent}`}>{children}</div>
      {sub && <div className="text-[9px] font-mono uppercase tracking-wider text-slate-600 mt-2">{sub}</div>}
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} aria-label={ariaLabel || label}
        className={`${base} block hover:border-[#E5B842] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B842]/60`}
        style={{ transition: "border-color 0.18s ease, transform 0.18s ease" }}>
        {inner}
      </Link>
    );
  }
  return <div data-testid={testid} className={base}>{inner}</div>;
}

function RailTab({ active, onClick, Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      aria-current={active ? "page" : undefined}
      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-heading font-bold uppercase tracking-wide ${active ? "bg-[#1a1407] text-[#E5B842] border border-[#E5B842]/40" : "text-slate-400 hover:text-[#E5B842] border border-transparent"}`}
      style={{ transition: "color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease" }}
    >
      <Icon className="w-4 h-4 shrink-0" /> {label}
    </button>
  );
}

function VerificationCard({ player }) {
  const verifyLink = `${SITE_ORIGIN}/verify/player/${player.wsbf_player_id}`;
  return (
    <div className="rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.18)] p-5 flex items-center justify-between gap-4" data-testid="passport-verification">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-1 flex items-center gap-1">
          <QrCode className="w-3 h-3" /> Scan to verify
        </div>
        <Link to={`/verify/player/${player.wsbf_player_id}`} className="text-xs text-slate-400 hover:text-[#E5B842] font-mono break-all" style={{ transition: "color 0.15s ease" }}>
          /verify/player/{player.wsbf_player_id}
        </Link>
      </div>
      <img data-testid="passport-qr" src={qrUrl(verifyLink)} alt="verification QR" className="w-20 h-20 rounded-lg bg-white p-1.5 shrink-0" />
    </div>
  );
}

function ShopPromo() {
  return (
    <Link to="/shop" data-testid="passport-shop-promo" className="group block rounded-xl border border-[rgba(212,175,55,0.22)] bg-gradient-to-r from-[#1a1407] to-[#0c0c0c] p-5 flex items-center justify-between gap-4 hover:border-[#E5B842]/60" style={{ transition: "border-color 0.18s ease" }}>
      <div className="min-w-0">
        <div className="font-heading text-base font-black uppercase tracking-tight text-slate-50">Get more with WSTF Shop</div>
        <div className="text-xs text-slate-400 mt-0.5">Avatars · Packs · Exclusive cosmetics</div>
      </div>
      <span className="inline-flex items-center gap-1.5 shrink-0 px-4 py-2 rounded-lg text-xs font-heading font-bold uppercase tracking-wide bg-[#E5B842] text-black group-hover:bg-[#F0D78A]" style={{ transition: "background-color 0.15s ease" }}>
        <Store className="w-4 h-4" /> Visit Shop
      </span>
    </Link>
  );
}

function CareerStat({ label, value, sub }) {
  return (
    <div className="rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.16)] p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-1.5">{label}</div>
      <div className="font-heading text-2xl font-black text-slate-50 tabular-nums leading-none">{value}</div>
      {sub && <div className="text-[10px] font-mono text-slate-500 mt-1.5">{sub}</div>}
    </div>
  );
}

// Recent Activity — real settled duel results (W/L/D + points), lazy-fetched.
function RecentActivity({ wsbfId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/players/${wsbfId}/recent-results?limit=10`)
      .then((r) => { if (alive) setRows(r.data.results || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [wsbfId]);
  const chip = {
    W: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    L: "bg-rose-500/15 text-rose-300 border-rose-500/40",
    D: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  };
  return (
    <div className="rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.18)] p-5" data-testid="passport-recent-activity">
      <div className="font-heading text-sm font-black uppercase tracking-tight text-slate-100 flex items-center gap-2 mb-4">
        <ListChecks className="w-4 h-4 text-[#E5B842]" /> Recent Activity
      </div>
      {rows === null ? (
        <div className="py-8 text-center text-slate-500 font-mono text-xs uppercase tracking-widest" data-testid="recent-activity-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-slate-500 font-mono text-xs uppercase tracking-widest" data-testid="recent-activity-empty">No settled results yet</div>
      ) : (
        <div className="divide-y divide-[rgba(212,175,55,0.1)]">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-2.5" data-testid={`recent-result-${i}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-sm text-slate-200 font-semibold">
                  {r.opponent?.country && <Flag country={r.opponent.country} width={16} />}
                  <span className="truncate">vs {r.opponent?.short_name || r.opponent?.name || "—"}</span>
                </div>
                <div className="text-[11px] font-mono text-slate-500 truncate mt-0.5">
                  {r.competition} · {new Date(r.date).toLocaleDateString()}{r.odds ? ` · odds ${r.odds}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span data-testid={`recent-result-chip-${i}`} className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-black border ${chip[r.result] || chip.D}`}>{r.result}</span>
                <span className="font-heading text-lg font-black tabular-nums text-[#E5B842] w-9 text-right">{r.points > 0 ? `+${r.points}` : r.points}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlayerPassportPage() {
  const { wsbfId } = useParams();
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => {
    setPlayer(null);
    setNotFound(false);
    setError(false);
    api.get(`/players/${wsbfId}`)
      .then(({ data }) => setPlayer(data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else setError(true);
      });
  }, [wsbfId]);

  useEffect(() => { load(); }, [load]);

  if (notFound) {
    return (
      <div className={`${CONTAINER} py-16 animate-fade-up`}>
        <div data-testid="passport-not-found" className="text-center py-20 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.25)] rounded-xl">
          Player credential not found
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className={`${CONTAINER} py-16 animate-fade-up`}>
        <div data-testid="passport-error" className="text-center py-20 border border-dashed border-rose-500/30 rounded-xl">
          <AlertTriangle className="w-7 h-7 text-rose-400 mx-auto mb-3" />
          <div className="text-slate-300 font-mono text-sm uppercase tracking-widest mb-4">Could not load this credential</div>
          <button onClick={load} data-testid="passport-retry" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[rgba(212,175,55,0.3)] text-slate-200 hover:border-[#E5B842]/60 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
            <RotateCw className="w-4 h-4" /> Retry
          </button>
        </div>
      </div>
    );
  }
  if (!player) {
    return (
      <div className={`${CONTAINER} py-16`} data-testid="passport-loading">
        <div className="h-5 w-32 bg-[#141414] rounded mb-6 animate-pulse" />
        <div className="h-56 rounded-2xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.14)] animate-pulse" />
        <div className="grid lg:grid-cols-3 gap-4 mt-4">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-44 rounded-2xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.12)] animate-pulse" />)}
        </div>
      </div>
    );
  }

  const r = player.rating;
  const provisional = r && (r.is_provisional || !r.has_rating);
  const isOwner = user?.player_id && player?.id && user.player_id === player.id;
  const champion = (player.honours || []).find((h) => /World Champion/i.test(h.title));
  const clubName = player.club?.name;
  const c = player.career;
  const _nm = (player.display_name || "").trim();
  const _ls = _nm.lastIndexOf(" ");
  const firstName = _ls > 0 ? _nm.slice(0, _ls) : _nm;
  const lastName = _ls > 0 ? _nm.slice(_ls + 1) : "";
  const worldRankPos = r?.has_rating && !provisional && r.world_rank ? r.world_rank.position : null;
  const natPos = r?.has_rating && !provisional && r.national_rank ? r.national_rank.position : null;
  const ch = player.challenge;
  const _iso = player.country?.iso3 || "";
  const countryLabel = player.country?.name ? `${player.country.name} Rank` : "National Rank";
  const rankCountryLink = `/rankings?entity=players&ranking=arenabook&scope=country${_iso ? `&country=${_iso}` : ""}`;
  const arenabookLink = "/rankings?entity=players&ranking=arenabook&scope=world";
  const challengeLink = "/rankings?entity=players&ranking=challenge&scope=world";
  const cityIsCountry = player.city && player.country?.name && player.city.trim().toLowerCase() === player.country.name.trim().toLowerCase();

  return (
    <div className="animate-fade-up pb-24 xl:pb-16">
      {/* ===== Cinematic identity header ===== */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        {/* Shared stadium background (same asset for every player) — dark left → livelier right */}
        <img src="/assets/passport-header-bg.webp" alt="" aria-hidden="true" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-center" />
        {/* Left-dark readability scrim (left ~40%) — right side stays near-full clarity */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to right, #000000 0%, rgba(0,0,0,0.82) 22%, rgba(0,0,0,0.35) 42%, transparent 62%)" }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, transparent 40%, #000000 100%)" }} />
        <div className="absolute inset-0 mesh-grid opacity-[0.10] pointer-events-none" />
        <div className={`${CONTAINER} relative pt-8 pb-10 lg:pt-14 lg:pb-16`}>
          <Link to="/players" data-testid="passport-back" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E5B842] mb-5" style={{ transition: "color 0.15s ease" }}>
            <ArrowLeft className="w-4 h-4" /> Back to registry
          </Link>
          <div className="flex items-start gap-4 sm:gap-6">
            <CinematicPortrait player={player} variant="card" testid="passport-avatar-wide"
              className="shrink-0 w-[92px] h-[116px] sm:w-[120px] sm:h-[150px] lg:w-[190px] lg:h-[240px]" />
            <div className="min-w-0 flex-1 lg:max-w-[64%]">
              {/* PLAYER label + status */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading font-extrabold uppercase tracking-[0.3em] text-[#E5B842] text-[11px]">Player</span>
                {player.is_simulated && <SimBadge />}
                <StatusBadge status={player.status} />
              </div>
              {/* Name: two lines (first / surname) — surname dominant, no flag (flag is in meta row) */}
              <h1 data-testid="passport-name" className="mt-1.5 font-heading font-black uppercase tracking-tight text-slate-50 leading-[0.95] wsbf-slant-block break-words">
                <span className="block text-2xl sm:text-3xl lg:text-4xl">{firstName}</span>
                {lastName && <span className="block text-3xl sm:text-4xl lg:text-5xl">{lastName}</span>}
              </h1>
              {/* WSTF ID alone */}
              <div data-testid="passport-wsbf-id" className="mt-2 font-mono font-bold tracking-[0.22em] text-[#E5B842] text-sm">{player.wsbf_player_id}</div>
              {/* Meta row: country · age · club */}
              <div className="mt-2.5 flex items-center gap-x-4 gap-y-1.5 flex-wrap text-sm text-slate-300">
                <span className="inline-flex items-center gap-1.5">
                  <Flag country={player.country} width={20} /><span className="text-slate-200">{player.country?.name}</span>
                  {player.city && !cityIsCountry && <span className="text-slate-500">· {player.city}</span>}
                </span>
                {player.age != null && (
                  <span data-testid="passport-age" className="inline-flex items-center gap-1.5 text-slate-300">
                    <User className="w-3.5 h-3.5 text-[#E5B842]" />{player.age} yrs
                  </span>
                )}
                {clubName ? (
                  <Link to={`/club/${player.club.id}`} data-testid="passport-club-link" className="inline-flex items-center gap-1.5 text-slate-200 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
                    <ClubCrest club={player.club} size={20} />{clubName} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                ) : (
                  <span data-testid="passport-free-agent" className="inline-flex items-center gap-1.5 text-slate-500 italic"><ShieldHalf className="w-3.5 h-3.5" />Free agent</span>
                )}
              </div>
              {champion && (
                <div data-testid="passport-world-champion-badge" className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-gradient-to-r from-[#3a2f0d] to-[#1a1405] text-[#E5B842] border border-[#E5B842]/50">
                  <Crown className="w-3.5 h-3.5" /> {champion.title}
                </div>
              )}
              {/* World rank + Share */}
              <div className="mt-4 flex items-center gap-4 flex-wrap">
                <div data-testid="passport-world-rank" className="inline-flex items-baseline gap-2">
                  <span className="font-heading font-extrabold uppercase tracking-[0.2em] text-[11px] text-slate-400">World Rank</span>
                  {worldRankPos ? (
                    <span data-testid="passport-world-rank-value" className="font-heading font-black text-2xl sm:text-3xl text-[#E5B842] leading-none inline-flex items-baseline gap-1">#{worldRankPos}<RankChange change={r.world_rank.change} /></span>
                  ) : (
                    <span className="font-heading font-black text-2xl text-slate-600 leading-none">—</span>
                  )}
                </div>
                <ShareButton kind="player" id={player.wsbf_player_id} testid="share-player-btn" />
              </div>
            </div>
            {/* Desktop signature tagline over the intense right side */}
            {player.tagline && (
              <div data-testid="passport-tagline" className="hidden lg:flex flex-1 items-center justify-end pl-6 self-center pointer-events-none">
                <p className="font-signature text-right text-5xl xl:text-6xl leading-[1.05] max-w-[440px] break-words" style={{ color: "#E5B842", textShadow: "0 2px 22px rgba(0,0,0,0.95)" }}>{player.tagline}</p>
              </div>
            )}
          </div>
          {/* Mobile signature tagline */}
          {player.tagline && (
            <p data-testid="passport-tagline-mobile" className="lg:hidden mt-4 font-signature text-3xl leading-tight break-words" style={{ color: "#E5B842", textShadow: "0 2px 12px rgba(0,0,0,0.85)" }}>{player.tagline}</p>
          )}

          {player.sanction_status && (
            <div data-testid="passport-sanction" className={`mt-5 rounded-xl p-4 border ${player.sanction_status.provisional ? "bg-amber-500/10 border-amber-500/40" : "bg-red-500/10 border-red-500/40"}`}>
              <div className={`font-heading text-lg font-black uppercase tracking-wide ${player.sanction_status.provisional ? "text-amber-300" : "text-red-400"}`}>
                {player.sanction_status.provisional ? "Provisional Measure — pending process" : (player.sanction_status.permanent ? "Banned" : "Suspended")}
              </div>
              <div className="text-xs font-mono text-slate-400 mt-1">
                {player.sanction_status.level} · scope {player.sanction_status.scope_type} {(player.sanction_status.scope_values || []).join(", ")}
                {player.sanction_status.expires_at ? ` · until ${new Date(player.sanction_status.expires_at).toLocaleDateString()}` : ""}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ===== Command-center grid: left player rail + workspace ===== */}
      <div className={`${CONTAINER} pt-6 grid grid-cols-1 xl:grid-cols-[220px_minmax(0,1fr)] gap-6`}>
        {/* Left rail (desktop) */}
        <aside className="hidden xl:block" data-testid="player-nav-rail">
          <div className="sticky top-6 space-y-1.5">
            {TABS.map((t) => (
              <RailTab key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} Icon={t.Icon} label={t.label} testid={`rail-tab-${t.key}`} />
            ))}
            <div className="h-px bg-[rgba(212,175,55,0.14)] my-3" />
            <Link to="/rankings" data-testid="rail-link-rankings" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-heading font-bold uppercase tracking-wide text-slate-400 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}><Trophy className="w-4 h-4" /> Rankings</Link>
            {clubName && <Link to={`/club/${player.club.id}`} data-testid="rail-link-club" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-heading font-bold uppercase tracking-wide text-slate-400 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}><ShieldHalf className="w-4 h-4" /> My Club</Link>}
            <Link to="/shop" data-testid="rail-link-shop" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-heading font-bold uppercase tracking-wide text-slate-400 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}><Store className="w-4 h-4" /> Shop</Link>
            {isOwner && <Link to="/settings" data-testid="rail-link-settings" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-heading font-bold uppercase tracking-wide text-slate-400 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}><SettingsIcon className="w-4 h-4" /> Settings</Link>}
          </div>
        </aside>

        {/* Workspace */}
        <main className="min-w-0">
          {/* 4 metric cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="passport-metrics">
            <MetricCard label={countryLabel} sub="Country standing" testid="metric-country-rank" Icon={MapPin} to={rankCountryLink} ariaLabel={`${player.country?.name || "National"} rankings`}>
              {natPos ? (<span data-testid="passport-national-rank" className="inline-flex items-baseline gap-1">#{natPos}<RankChange change={r.national_rank.change} /></span>) : <span className="text-slate-600">—</span>}
            </MetricCard>
            <MetricCard label="ArenaBook Ranking" sub="ArenaBook rating" accent="text-[#E5B842]" testid="metric-rating" Icon={Gauge} to={arenabookLink} ariaLabel="ArenaBook global rankings">
              {r?.has_rating ? (<span data-testid="passport-rating">{Math.round(r.rating)}<span className="text-[10px] font-mono text-slate-500 ml-1 align-top">±{Math.round(r.rd)}</span></span>) : <span className="text-lg text-slate-500">Unrated</span>}
            </MetricCard>
            <MetricCard label="Challenge Ranking" sub="Challenge rating" accent="text-fuchsia-300" testid="metric-challenge" Icon={Swords} to={challengeLink} ariaLabel="Challenge global rankings">
              {ch?.has_rating ? (<span data-testid="passport-challenge">{Math.round(ch.rating)}{ch.rd != null && <span className="text-[10px] font-mono text-slate-500 ml-1 align-top">±{Math.round(ch.rd)}</span>}</span>) : <span className="text-lg text-slate-500">Unrated</span>}
            </MetricCard>
            <MetricCard label="Value" sub="Player value" testid="metric-value" Icon={Coins}>
              <span data-testid="passport-value" className="text-slate-600">—</span>
            </MetricCard>
          </div>

          {/* Two wide boxes: Performance Overview + Current Season */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4" data-testid="passport-wide-boxes">
            <PerformanceOverviewBox wsbfId={wsbfId} />
            <CurrentSeasonBox wsbfId={wsbfId} />
          </div>

          {/* 2x2 per-ladder rating boxes (Overall / ArenaBook / Challenge / Strike 5) */}
          <LadderRatingGrid player={player} />

          {/* Full-width Club & Transfers */}
          <div className="mt-3 sm:mt-4">
            <PlayerTransferPanel wsbfId={wsbfId} player={player} />
          </div>

          {/* ===== Tab content (section chosen from the left rail / mobile bottom nav) ===== */}
          <div className="mt-6">
            {tab === "overview" && (
              <div className="space-y-4" data-testid="tab-content-overview">
                <RecentActivity wsbfId={wsbfId} />
                <SportsbookPassport wsbfId={wsbfId} />
                <ConnectedActivity wsbfId={wsbfId} />
                <VerificationCard player={player} />
                <ShopPromo />
              </div>
            )}

            {tab === "statistics" && (
              <div className="space-y-4" data-testid="tab-content-statistics">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                  <OverallCard overall={player.overall} />
                  <Strike5Card strike5={player.strike5} />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                  <DisciplineCard block={player.challenge} label="Challenge" accent="text-fuchsia-300" testid="challenge-rating-card-stats" />
                  <PlayerSeasonRacePanel wsbfId={wsbfId} />
                </div>
                {player.overall_showcase && <OverallShowcaseTimeline showcase={player.overall_showcase} />}
              </div>
            )}

            {tab === "achievements" && (
              <div className="space-y-4" data-testid="tab-content-achievements">
                <RecordsHeld records={player.records_held} testid="player-records-held" />
                {(player.honours || []).length > 0 ? (
                  <div className="rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.18)] p-5" data-testid="passport-honours">
                    <div className="font-heading text-sm font-black uppercase tracking-tight text-slate-100 flex items-center gap-2 mb-3"><Award className="w-4 h-4 text-[#E5B842]" /> Honours</div>
                    <div className="space-y-2">
                      {player.honours.map((h, i) => (
                        <div key={i} className="flex items-center gap-2.5 text-sm text-slate-200" data-testid={`honour-${i}`}>
                          <Trophy className="w-4 h-4 text-[#E5B842] shrink-0" />
                          <span className="break-words">{h.title}{h.season ? ` · ${h.season}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-[rgba(212,175,55,0.25)] p-8 text-center text-slate-500 font-mono text-sm uppercase tracking-widest" data-testid="honours-empty">No honours recorded yet</div>
                )}
                <CollectionShowcase collection={player.collection} />
              </div>
            )}

            {tab === "career" && (
              <div className="space-y-4" data-testid="tab-content-career">
                {c && c.duels_played > 0 ? (
                  <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <CareerStat label="Career Duels" value={<span data-testid="passport-career">{c.duels_won}-{c.duels_lost}-{c.duels_split}</span>} sub={`${c.duels_played} played`} />
                      <CareerStat label="Team Points" value={c.team_points_generated} sub="Generated" />
                      <CareerStat label="Peak Rating" value={c.peak_rating ?? "—"} sub={c.peak_world_rank ? `Peak #${c.peak_world_rank} world` : null} />
                      <CareerStat label="Highest Odds" value={c.highest_winning_odds || "—"} sub={c.average_winning_odds ? `avg ${c.average_winning_odds}` : null} />
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <CareerStat label="1pt Wins" value={c.wins_1pt} />
                      <CareerStat label="2pt Wins" value={c.wins_2pt} />
                      <CareerStat label="3pt Wins" value={c.wins_3pt} />
                      {c.national_team_caps > 0 ? (
                        <div className="rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.16)] p-4" data-testid="passport-nt-caps">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-1.5">National Team</div>
                          <div className="flex items-center gap-2">
                            {(c.national_team_memberships || []).map((m) => <Flag key={m.id} country={m} width={22} className="inline-block" />)}
                            <span className="font-heading text-2xl font-black text-[#E5B842] tabular-nums">{c.national_team_caps}</span>
                            <span className="text-[10px] font-mono uppercase text-slate-500">caps</span>
                          </div>
                        </div>
                      ) : <CareerStat label="National Team" value="—" sub="No caps" />}
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl border border-dashed border-[rgba(212,175,55,0.25)] p-8 text-center text-slate-500 font-mono text-sm uppercase tracking-widest" data-testid="career-empty">No career activity yet</div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ===== Mobile bottom nav (highest-frequency real destinations) ===== */}
      <nav className="xl:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0a0a0a]/95 backdrop-blur border-t border-[rgba(212,175,55,0.22)]" data-testid="player-bottom-nav">
        <div className="grid grid-cols-5">
          {[{ key: "overview", label: "Profile", Icon: User }, { key: "statistics", label: "Stats", Icon: BarChart3 }, { key: "achievements", label: "Honours", Icon: Award }, { key: "career", label: "Career", Icon: ListChecks }].map((t) => (
            <button key={t.key} onClick={() => { setTab(t.key); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`bottomnav-${t.key}`}
              className={`flex flex-col items-center gap-1 py-2.5 text-[10px] font-mono uppercase tracking-wider ${tab === t.key ? "text-[#E5B842]" : "text-slate-500"}`} style={{ transition: "color 0.15s ease" }}>
              <t.Icon className="w-5 h-5" /> {t.label}
            </button>
          ))}
          <Link to="/shop" data-testid="bottomnav-shop" className="flex flex-col items-center gap-1 py-2.5 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
            <Store className="w-5 h-5" /> Shop
          </Link>
        </div>
      </nav>
    </div>
  );
}
